import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { Badge } from '../ui/components/Badge';
import { Leaderboard } from '../components/sweepstakes/Leaderboard';
import { sweepstakesApi, type ContestView } from '../api/sweepstakes';
import { useMyEntries } from '../hooks/useSweepstakes';
import { useAuthStore } from '../store/authStore';

function formatCountdownValue(targetDate: string): string {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function useCountdown(targetDate: string | undefined): string {
  const [display, setDisplay] = useState(() => targetDate ? formatCountdownValue(targetDate) : '');
  useEffect(() => {
    if (!targetDate) return;
    setDisplay(formatCountdownValue(targetDate));
    const interval = setInterval(() => setDisplay(formatCountdownValue(targetDate)), 60_000);
    return () => clearInterval(interval);
  }, [targetDate]);
  return display;
}

function formatXrp(drops: string): string {
  const xrp = Number(drops) / 1_000_000;
  return xrp > 0 ? `${xrp.toLocaleString()} XRP` : 'None';
}

export const SweepstakesPage: React.FC = () => {
  const { slug, contestId } = useParams<{ slug: string; contestId?: string }>();
  const [contestView, setContestView] = useState<ContestView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        let data: ContestView | { contest: null };
        if (contestId) {
          data = await sweepstakesApi.getById(contestId);
        } else if (slug) {
          data = await sweepstakesApi.getBySlug(slug);
        } else {
          setError('Missing collection slug');
          return;
        }
        setContestView(data.contest ? data as ContestView : null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug, contestId]);

  const contest = contestView?.contest;
  const { entryCount, rank } = useMyEntries(contest?.id);

  const isMintContest = contest?.contest_type === 'mint_rewards';
  const isActive = contest?.status === 'active';
  const isEnded = contest ? ['ended', 'resolved'].includes(contest.status) : false;
  const isScheduled = contest?.status === 'scheduled' || contest?.status === 'snapshot_pending';
  const countdownTarget = isScheduled ? contest?.starts_at : contest?.ends_at;
  const countdown = useCountdown(!isEnded ? countdownTarget : undefined);

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="text-center py-12 text-subtext-color">Loading contest...</div>
      </DefaultPageLayout>
    );
  }

  if (error || !contestView || !contest) {
    return (
      <DefaultPageLayout>
        <div className="text-center py-12">
          <p className="text-subtext-color mb-4">{error || 'No contest found for this collection.'}</p>
          {slug && (
            <Link to={`/collections/${slug}`}>
              <Button variant="neutral-secondary">Back to Collection</Button>
            </Link>
          )}
        </div>
      </DefaultPageLayout>
    );
  }

  const statusLabel = isActive ? 'Active' : isEnded ? 'Ended' : isScheduled ? 'Upcoming' : contest.status;
  const statusVariant = isActive ? 'success' : isEnded ? 'neutral' : 'warning';

  return (
    <DefaultPageLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Back link */}
        {slug && (
          <Link to={`/collections/${slug}`} className="text-sm text-brand-600 hover:underline mb-4 inline-block">
            &larr; Back to Collection
          </Link>
        )}

        {/* Contest Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant={statusVariant as any}>{statusLabel}</Badge>
          </div>
          <h1 className="text-2xl font-bold text-default-font mb-2">{contest.name}</h1>
          {contest.description && (
            <p className="text-subtext-color">{contest.description}</p>
          )}
        </div>

        {/* Status / Timing */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-3 bg-default-background rounded-lg border border-neutral-border">
            <div className="text-xs text-subtext-color uppercase">
              {isScheduled ? 'Starts In' : isActive ? 'Ends In' : 'Duration'}
            </div>
            <div className="text-lg font-semibold text-default-font">
              {isEnded
                ? `${new Date(contest.starts_at).toLocaleDateString()} - ${new Date(contest.ends_at).toLocaleDateString()}`
                : countdown
              }
            </div>
          </div>
          <div className="p-3 bg-default-background rounded-lg border border-neutral-border">
            <div className="text-xs text-subtext-color uppercase">Participants</div>
            <div className="text-lg font-semibold text-default-font">
              {contestView.stats.participants}
            </div>
          </div>
        </div>

        {/* Rules */}
        <div className="mb-6 p-4 bg-default-background rounded-lg border border-neutral-border">
          <h3 className="font-semibold text-default-font mb-2">Rules</h3>
          <ul className="text-sm text-subtext-color space-y-1">
            {isMintContest ? (
              <>
                <li>Mint NFTs from this drop to earn entries</li>
                <li>Each mint = 1 entry</li>
                <li>Mint more for more chances to win</li>
              </>
            ) : (
              <>
                <li>Buy NFTs that were listed before the contest snapshot to earn entries</li>
                <li>Each unique NFT purchased = 1 entry per wallet</li>
                {Number(contest.min_price_drops) > 0 && (
                  <li>Minimum purchase price: {formatXrp(contest.min_price_drops)}</li>
                )}
                <li>Purchases through recognized marketplaces only</li>
              </>
            )}
          </ul>
        </div>

        {/* My Stats */}
        {user && (isActive || isEnded) && (
          <div className="mb-6 p-4 bg-default-background rounded-lg border border-brand-600">
            <h3 className="font-semibold text-default-font mb-1">Your Stats</h3>
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-subtext-color">Entries: </span>
                <span className="font-semibold text-default-font">{entryCount}</span>
              </div>
              {rank && (
                <div>
                  <span className="text-subtext-color">Rank: </span>
                  <span className="font-semibold text-default-font">#{rank}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prize Tiers */}
        <div className="mb-6">
          <h3 className="font-semibold text-default-font mb-3">Prizes</h3>
          <div className="space-y-3">
            {contestView.prizeTiers.map(tier => {
              const tierWinners = contestView.winners?.filter(w => w.prize_tier_id === tier.id) || [];
              return (
                <div key={tier.id} className="p-3 bg-default-background rounded-lg border border-neutral-border">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={tier.type === 'leaderboard' ? 'success' : 'warning'}>
                      {tier.type === 'leaderboard' ? `Top ${tier.winner_count}` : `Raffle (${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''})`}
                    </Badge>
                    <span className="font-semibold text-default-font">{tier.name}</span>
                  </div>
                  <p className="text-sm text-subtext-color">{tier.description}</p>
                  {tierWinners.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {tierWinners.map(w => (
                        <div key={w.id} className="text-sm text-brand-600 font-medium">
                          Winner: {w.wallet_address.slice(0, 6)}...{w.wallet_address.slice(-4)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <h3 className="font-semibold text-default-font mb-3">
            {isEnded ? 'Final Leaderboard' : isMintContest ? 'Minter Leaderboard' : 'Live Leaderboard'}
          </h3>
          <Leaderboard contestId={contest.id} frozen={isEnded} />
        </div>

        {/* CTA */}
        {isActive && slug && (
          <div className="mt-6 text-center">
            {isMintContest ? (
              <Link to={`/collections/${slug}`}>
                <Button variant="brand-primary">
                  Mint Now
                </Button>
              </Link>
            ) : (
              <Link to={`/collections/${slug}?contestEligible=true`}>
                <Button variant="brand-primary">
                  Browse Eligible NFTs
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
